import type { ComponentValueResponse, SustainabilityComponentInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicPortfolioSustainabilityState,
  getPublicSustainabilityValue,
  savePublicSustainability,
} from "@/lib/api/public-flow/sustainability";
import { useSessionToken } from "@/lib/context/public-session-context";
import { useInvalidatePublicDynamicSync } from "@/lib/hooks/use-public-flow/use-portfolios";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";

export const publicSustainabilityKeys = {
  value: ["publicFlow", "sustainability", "flow-instance-value"] as const,
  portfolioState: (portfolioId: string | null) =>
    ["publicFlow", "sustainability", "portfolio-state", portfolioId] as const,
  allPortfolioStates: ["publicFlow", "sustainability", "portfolio-state"] as const,
};

export function usePublicSustainabilityValue() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: publicSustainabilityKeys.value,
    queryFn: () => getPublicSustainabilityValue(token),
    enabled: !!token,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSavePublicSustainability() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  const queryKey = publicSustainabilityKeys.value;
  return useMutation({
    mutationFn: (input: SustainabilityComponentInput) => savePublicSustainability(token, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ComponentValueResponse>(queryKey);
      queryClient.setQueryData<ComponentValueResponse>(queryKey, (old) => ({
        inputData: JSON.stringify(input),
        calculatedData: old?.calculatedData ?? null,
      }));
      return { previousData };
    },
    onError: (_err, _input, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(queryKey, {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Completion semantics — keep the public progress bar/markers live.
        queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
        // The save re-ran the matching cascade — refresh every portfolio's state.
        queryClient.invalidateQueries({ queryKey: publicSustainabilityKeys.allPortfolioStates });
        // QP-185 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
  });
}

export function usePublicPortfolioSustainabilityState(portfolioId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: publicSustainabilityKeys.portfolioState(portfolioId),
    queryFn: () => getPublicPortfolioSustainabilityState(token, portfolioId!),
    enabled: !!token && !!portfolioId,
  });
  return { state: data ?? null, error, isLoading };
}
